import React, { useContext, useLayoutEffect, useRef, useState } from "react";
import {
  Button as AntdButton,
  Col,
  Row,
  ButtonProps as AntdButtonProps,
  message,
  Select,
  Spin,
  Table,
  Input,
  Tooltip,
} from "antd";
import { MdOutlineMoveDown } from "react-icons/md";
import { useModal } from "react-modal-hook";
import { useTranslation } from "react-i18next";
import ProductContext from "../../../../context/product/context";
import FormHookModal from "../../modals/form-hook-modal";
import FormItem from "../../form-item";
import { IQuantity } from "../../../../models/product/response";
import EndPoints from "../../../../services/end-points";
import { IBatch } from "../../../../models/wms/batch/response";
import { formatBatchPurchasePriceOf } from "../../../../utils/helpers/batch-purchase-price";
import WmsStorablePicker, {
  IStorableSelection,
} from "../../../wms/wms-storable-picker";

export interface ButtonProps extends AntdButtonProps {
  passedId: number;
  quantities: IQuantity[];
  productName?: string;
}

const getStoreName = (name: string | { ar: string; en: string }): string => {
  if (typeof name === "string") return name;
  return name?.ar || name?.en || "-";
};

const formatStorableLocation = (storable: IBatch["storable"]): string => {
  if (!storable) return "-";
  const parts = [
    storable.zone?.name,
    storable.aisle?.name,
    storable.storageUnit?.name,
    storable.shelf?.name,
    storable.name,
  ].filter(Boolean) as string[];
  if (parts.length === 0) return storable.name ?? "-";
  return parts.join(" / ");
};

interface IBatchRow extends IBatch {
  transferring: boolean;
}

const TransferBatchButton: React.FC<ButtonProps> = ({
  passedId,
  quantities,
  productName,
  ...props
}) => {
  const { t } = useTranslation();
  const { actions } = useContext(ProductContext);

  const [showFormModal, hideFormModal] = useModal(() => {
    const [selectedStoreId, setSelectedStoreId] = useState<number | null>(null);
    const [loadingBatches, setLoadingBatches] = useState(false);
    const [rows, setRows] = useState<IBatchRow[]>([]);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [total, setTotal] = useState(0);
    const [transferTarget, setTransferTarget] = useState<
      IStorableSelection | undefined
    >();
    const [transferReason, setTransferReason] = useState("");
    const [selectedBatchId, setSelectedBatchId] = useState<number | null>(null);
    const tableWrapRef = useRef<HTMLDivElement>(null);
    const [tableScrollY, setTableScrollY] = useState(280);

    const loadBatches = async (opts?: {
      storeId?: number;
      page?: number;
      perPage?: number;
    }) => {
      const storeId = opts?.storeId ?? selectedStoreId;
      const nextPage = opts?.page ?? page;
      const nextPerPage = opts?.perPage ?? perPage;
      if (!storeId) return;

      setLoadingBatches(true);
      try {
        const { data } = await EndPoints.wmsBatch.getAllBatches({
          productId: passedId,
          storeId,
          hasStock: true,
          page: nextPage,
          perPage: nextPerPage,
        });
        const batches: IBatch[] = data?.data ?? [];
        setTotal(data?.total ?? 0);
        setPage(data?.currentPage ?? nextPage);
        setRows(batches.map((b) => ({ ...b, transferring: false })));
      } catch (error: any) {
      } finally {
        setLoadingBatches(false);
      }
    };

    const handleStoreChange = async (storeId: number) => {
      setSelectedStoreId(storeId);
      setRows([]);
      setTotal(0);
      setPage(1);
      setSelectedBatchId(null);
      setTransferTarget(undefined);
      if (!storeId) return;
      await loadBatches({ storeId, page: 1 });
    };

    const handleTransfer = async () => {
      if (!selectedBatchId) {
        message.error(t("please_select_batch"));
        return;
      }
      if (!transferTarget) {
        message.error(t("please_select_storage_location"));
        return;
      }

      setRows((prev) =>
        prev.map((r) =>
          r.id === selectedBatchId ? { ...r, transferring: true } : r,
        ),
      );

      try {
        await EndPoints.wmsBatch.transferBatch(selectedBatchId, {
          storableType: transferTarget.storableType,
          storableId: transferTarget.storableId,
          reason: transferReason || undefined,
        });
        message.success(t("transfer_successful"));
        actions.getData();
        hideFormModal();
      } catch (error: any) {
        setRows((prev) =>
          prev.map((r) =>
            r.id === selectedBatchId ? { ...r, transferring: false } : r,
          ),
        );
      }
    };

    useLayoutEffect(() => {
      const el = tableWrapRef.current;
      if (!el) return;
      const measure = () => {
        const h = el.clientHeight;
        setTableScrollY(Math.max(120, Math.floor(h - 112)));
      };
      measure();
      const ro = new ResizeObserver(measure);
      ro.observe(el);
      return () => ro.disconnect();
    }, [selectedStoreId, selectedBatchId, loadingBatches, rows.length]);

    const selectedBatch = rows.find((r) => r.id === selectedBatchId);
    const isTransferring = selectedBatch?.transferring ?? false;

    const columns = [
      {
        title: t("id"),
        dataIndex: "id",
        key: "id",
        width: 72,
        align: "center" as const,
      },
      {
        title: t("purchase_date"),
        dataIndex: "purchaseDate",
        key: "purchaseDate",
        width: 148,
        align: "center" as const,
        render: (val: string) => val ?? "-",
      },
      {
        title: t("expiration_date"),
        dataIndex: "expirationDate",
        key: "expirationDate",
        width: 148,
        align: "center" as const,
        render: (val: string) => val ?? "-",
      },
      {
        title: t("available"),
        dataIndex: "availableQuantity",
        key: "availableQuantity",
        width: 96,
        align: "center" as const,
      },
      {
        // The transfer copies this cost to the destination batch untouched, so
        // it is shown here rather than left to be discovered afterwards.
        title: t("batch_purchase_price"),
        key: "purchasePrice",
        width: 140,
        align: "center" as const,
        render: (_: any, record: IBatchRow) =>
          formatBatchPurchasePriceOf(record, t("currency")),
      },
      {
        title: t("current_location"),
        key: "location",
        width: 220,
        align: "center" as const,
        onCell: () => ({
          style: { overflow: "hidden", maxWidth: 220, verticalAlign: "middle" },
        }),
        render: (_: any, record: IBatchRow) => {
          const text = formatStorableLocation(record.storable);
          return (
            <Tooltip title={text}>
              <div
                style={{
                  width: "100%",
                  maxWidth: "100%",
                  margin: "0 auto",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  textAlign: "center",
                  boxSizing: "border-box",
                }}
              >
                {text}
              </div>
            </Tooltip>
          );
        },
      },
    ];

    return (
      <FormHookModal
        id="transfer-batch-modal"
        confirmButtonText={t("transfer")}
        loading={isTransferring}
        title={t("transfer_batch")}
        subtitle={
          productName ? `${t("product_name")}: ${productName}` : undefined
        }
        hideModal={hideFormModal}
        width="80vw"
        bodyScroll
        style={{ marginTop: "1.6rem", maxHeight: "85vh" }}
        onSubmit={() => {}}
        confirmClick={handleTransfer}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 16,
            width: "100%",
            minWidth: 0,
            height: "calc(85vh - 180px)",
            overflowY: "auto",
            overflowX: "hidden",
            paddingRight: 4,
          }}
        >
          <div style={{ flexShrink: 0 }}>
            <FormItem label={t("store")}>
              <Select
                style={{ width: "100%" }}
                value={selectedStoreId}
                onChange={handleStoreChange}
                options={quantities.map((q) => ({
                  value: q.store_id,
                  label: getStoreName(q.store_name),
                }))}
              />
            </FormItem>
          </div>

          {selectedStoreId &&
            (loadingBatches ? (
              <Row justify="center" style={{ flexShrink: 0 }}>
                <Spin />
              </Row>
            ) : (
              <div
                ref={tableWrapRef}
                style={{ flex: 1, minHeight: 0, overflow: "hidden" }}
              >
                <Table
                  rowKey="id"
                  size="small"
                  dataSource={rows}
                  columns={columns}
                  onRow={(record) => ({
                    onClick: () => setSelectedBatchId(record.id),
                    style: { cursor: "pointer" },
                  })}
                  pagination={{
                    current: page,
                    pageSize: perPage,
                    total,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showLessItems: true,
                    showTotal: (tot, range) =>
                      `${range[0]}-${range[1]} / ${tot}`,
                    onChange: (nextPage, nextPageSize) => {
                      const normalizedPageSize = nextPageSize ?? perPage;
                      setPerPage(normalizedPageSize);
                      setPage(nextPage);
                      setSelectedBatchId(null);
                      loadBatches({
                        page: nextPage,
                        perPage: normalizedPageSize,
                      });
                    },
                  }}
                  rowSelection={{
                    type: "radio",
                    selectedRowKeys: selectedBatchId ? [selectedBatchId] : [],
                    onChange: (keys) =>
                      setSelectedBatchId((keys[0] as number) ?? null),
                  }}
                  scroll={{ x: 1240, y: Math.max(180, tableScrollY) }}
                  locale={{ emptyText: t("no_batches") }}
                />
              </div>
            ))}

          {selectedBatchId && (
            <div style={{ flexShrink: 0 }}>
              <div style={{ fontWeight: 500 }}>{t("destination_location")}</div>
              <WmsStorablePicker
                storeId={selectedStoreId!}
                onStorableSelected={setTransferTarget}
              />
              <FormItem label={t("reason")}>
                <Input
                  value={transferReason}
                  onChange={(e) => setTransferReason(e.target.value)}
                  placeholder={t("optional")}
                />
              </FormItem>
            </div>
          )}
        </div>
      </FormHookModal>
    );
  }, [passedId, quantities, productName]);

  return (
    <AntdButton
      type="primary"
      icon={<MdOutlineMoveDown />}
      onClick={showFormModal}
      {...props}
    />
  );
};

export default TransferBatchButton;
